// frontend_react/src/context/AdminScopeContext.jsx

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import PropTypes from "prop-types";

import { useCompany } from "./CompanyContext";

const AdminScopeContext = createContext(null);

export function AdminScopeProvider({ children }) {
  const {
    companyUuid,
    companyName,
    isSuperAdmin,
  } = useCompany();

  const [selectedCompanyUuid, setSelectedCompanyUuid] =
    useState(companyUuid);

  const [selectedActivityUuid, setSelectedActivityUuid] =
    useState(null);

  const [selectedActivityName, setSelectedActivityName] =
    useState(null);

  const [selectedCompanyName, setSelectedCompanyName] =
    useState(companyName);

  useEffect(() => {
    if (!isSuperAdmin) {
      setSelectedCompanyUuid(companyUuid);
      setSelectedActivityUuid(null);
      setSelectedActivityName(null);
      setSelectedCompanyName(companyName);
    }
  }, [
    isSuperAdmin,
    companyUuid,
    companyName,
  ]);

  const value = useMemo(
    () => ({
      currentCompanyUuid: companyUuid,
      currentCompanyName: companyName,

      selectedCompanyUuid,
      selectedCompanyName,

      selectedActivityUuid,
      selectedActivityName,

      effectiveActivityUuid:
        selectedActivityUuid || null,
      effectiveActivityName:
        selectedActivityName || null,

      hasSelectedActivity:
        Boolean(selectedActivityUuid),

      setSelectedActivity: (activity) => {
        const nextActivityUuid =
          activity?.uuid || null;

        const nextActivityName =
          activity?.name || null;

        setSelectedActivityUuid(
          nextActivityUuid
        );
        setSelectedActivityName(
          nextActivityName
        );
      },

      clearSelectedActivity: () => {
        setSelectedActivityUuid(null);
        setSelectedActivityName(null);
      },

      effectiveCompanyUuid:
        selectedCompanyUuid || companyUuid,
      effectiveCompanyName:
        selectedCompanyName || companyName,

      canSwitchCompany: isSuperAdmin,
      hasCompanyScope:
        Boolean(selectedCompanyUuid || companyUuid),

      isGlobalScope:
        isSuperAdmin &&
        selectedCompanyUuid !== companyUuid,

      setSelectedCompany: (
        uuid,
        name = null
      ) => {
        if (!isSuperAdmin) {
          return;
        }

        if (uuid !== selectedCompanyUuid) {
          setSelectedActivityUuid(null);
          setSelectedActivityName(null);
        }

        setSelectedCompanyUuid(uuid);
        setSelectedCompanyName(name);
      },

      resetScope: () => {
        setSelectedCompanyUuid(companyUuid);
        setSelectedCompanyName(companyName);
        setSelectedActivityUuid(null);
        setSelectedActivityName(null);
      },
    }),
    [
      selectedCompanyUuid,
      selectedCompanyName,
      selectedActivityUuid,
      selectedActivityName,
      companyUuid,
      companyName,
      isSuperAdmin,
    ]
  );

  return (
    <AdminScopeContext.Provider value={value}>
      {children}
    </AdminScopeContext.Provider>
  );
}

AdminScopeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAdminScope() {
  const context = useContext(
    AdminScopeContext
  );

  if (!context) {
    throw new Error(
      "useAdminScope must be used within AdminScopeProvider"
    );
  }

  return context;
}